import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../../utils/api';
import useIsMobile from '../../utils/useIsMobile';

const WIDGET_DEFS = [
  { id: 'ticket_status',  label: 'Ticket Status' },
  { id: 'response_time',  label: 'Avg Response Time' },
  { id: 'closure_time',   label: 'Avg Closure Time' },
  { id: 'ticket_count',   label: 'Number of Tickets' },
  { id: 'trend_chart',    label: 'Ticket Trend' },
  { id: 'sla_compliance', label: 'SLA Compliance' },
  { id: 'resolution_rate',label: 'Resolution Rate' },
];

const DEFAULT_LAYOUT = ['ticket_status', 'sla_compliance', 'resolution_rate', 'response_time', 'closure_time', 'ticket_count', 'trend_chart'];

const PERIOD_OPTS = [
  { value: 'last_month', label: 'Last Month' },
  { value: 'this_month', label: 'This Month' },
  { value: 'this_fy',    label: 'This FY' },
];

function fmt(h) {
  if (h == null || isNaN(h)) return '—';
  if (h < 1) return `${Math.round(h * 60)}m`;
  return `${h.toFixed(1)}h`;
}

function PeriodPicker({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {PERIOD_OPTS.map(p => (
        <label key={p.value} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 11, fontWeight: 600, color: value === p.value ? 'var(--status-resolved-text)' : 'var(--text-secondary)', background: value === p.value ? 'var(--action-bg)' : 'var(--bg-app)', border: `1px solid ${value === p.value ? 'var(--action-hover)' : 'var(--border-subtle)'}`, borderRadius: 20, padding: '3px 10px', userSelect: 'none', transition: 'all .15s' }}>
          <input type="radio" name={`period-${p.value}`} checked={value === p.value} onChange={() => onChange(p.value)} style={{ display: 'none' }} />
          {p.label}
        </label>
      ))}
    </div>
  );
}

// ── Widgets ────────────────────────────────────────────────────────────────────

function StatBar({ label, value, total, color }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, fontSize: 11, fontWeight: 600 }}>
        <span style={{ color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{label}</span>
        <span style={{ color }}>{value} <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>({pct}%)</span></span>
      </div>
      <div style={{ position: 'relative', background: 'var(--n-100)', borderRadius: 5, height: 8 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 5, transition: 'width .4s' }} />
      </div>
    </div>
  );
}

function TicketStatusWidget({ data, period, onPeriodChange }) {
  const d = data?.[period] || {};
  const total = d.total ?? 0;
  const items = [
    { label: 'Raised',   value: total,            color: '#2563eb', bg: 'var(--blue-50, #eff6ff)' },
    { label: 'Resolved', value: d.resolved ?? 0,  color: '#10b981', bg: 'var(--action-bg)' },
    { label: 'Closed',   value: d.closed_only ?? 0, color: '#0B6623', bg: 'var(--action-bg)' },
    { label: 'Pending',  value: d.pending ?? (((d.open ?? 0) + (d.in_progress ?? 0))), color: '#d97706', bg: 'var(--status-progress-bg)' },
  ];
  return (
    <div>
      <div style={{ marginBottom: 12 }}><PeriodPicker value={period} onChange={onPeriodChange} /></div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 14 }}>
        {items.map(s => (
          <div key={s.label} style={{ background: s.bg, borderRadius: 10, padding: '14px 10px', textAlign: 'center', border: `1px solid ${s.color}22` }}>
            <div style={{ fontSize: 28, fontWeight: 900, fontFamily: 'Impact, Arial Narrow, sans-serif', color: s.color, lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 4, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .4 }}>{s.label}</div>
            <div style={{ fontSize: 10, color: s.color, marginTop: 2, fontWeight: 600 }}>{s.label === 'Raised' ? ' ' : `${total > 0 ? Math.round((s.value / total) * 100) : 0}%`}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-secondary)', borderTop: '1px solid var(--border-subtle)', paddingTop: 8 }}>
        <span>Total: <strong style={{ color: 'var(--text-primary)' }}>{total}</strong></span>
        <span>SLA Breached: <strong style={{ color: '#dc2626' }}>{d.breached ?? 0}</strong></span>
      </div>
    </div>
  );
}

function ResponseTimeWidget({ data, period, onPeriodChange }) {
  const d = data?.[period] || {};
  const val = fmt(d.avg_response_hours);
  const hasData = d.avg_response_hours != null;
  const color = !hasData ? 'var(--text-secondary)' : d.avg_response_hours <= 4 ? '#10b981' : d.avg_response_hours <= 24 ? '#f59e0b' : '#dc2626';
  const rating = !hasData ? '—' : d.avg_response_hours <= 4 ? 'Excellent' : d.avg_response_hours <= 24 ? 'Good' : 'Needs Attention';
  return (
    <div>
      <div style={{ marginBottom: 14 }}><PeriodPicker value={period} onChange={onPeriodChange} /></div>
      <div style={{ textAlign: 'center', padding: '16px 0 10px' }}>
        <div style={{ fontSize: 52, fontWeight: 900, color, letterSpacing: -2, fontFamily: 'Impact, Arial Narrow, sans-serif', lineHeight: 1 }}>{val}</div>
        <div style={{ fontSize: 11, fontWeight: 700, color, marginTop: 4, textTransform: 'uppercase', letterSpacing: .5 }}>{rating}</div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>Time from ticket creation → first agent reply</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 10, borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
        {[{ label: 'This Month', key: 'this_month' }, { label: 'Last Month', key: 'last_month' }, { label: 'This FY', key: 'this_fy' }].map(p => {
          const v = data?.[p.key]?.avg_response_hours;
          const c = v == null ? 'var(--text-secondary)' : v <= 4 ? '#10b981' : v <= 24 ? '#f59e0b' : '#dc2626';
          return (
            <div key={p.key} style={{ textAlign: 'center', opacity: period === p.key ? 1 : 0.5, background: 'var(--bg-app)', borderRadius: 8, padding: '8px 4px' }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: c }}>{fmt(v)}</div>
              <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 2 }}>{p.label}</div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text-secondary)', display: 'flex', gap: 12, justifyContent: 'center' }}>
        <span style={{ color: '#10b981' }}>● ≤4h Excellent</span>
        <span style={{ color: '#f59e0b' }}>● ≤24h Good</span>
        <span style={{ color: '#dc2626' }}>● &gt;24h Needs work</span>
      </div>
    </div>
  );
}

function ClosureTimeWidget({ data, period, onPeriodChange }) {
  const d = data?.[period] || {};
  const val = fmt(d.avg_closure_hours);
  const hasData = d.avg_closure_hours != null;
  const color = !hasData ? 'var(--text-secondary)' : d.avg_closure_hours <= 24 ? '#10b981' : d.avg_closure_hours <= 72 ? '#f59e0b' : '#dc2626';
  const rating = !hasData ? '—' : d.avg_closure_hours <= 24 ? 'Excellent' : d.avg_closure_hours <= 72 ? 'Good' : 'Needs Attention';
  return (
    <div>
      <div style={{ marginBottom: 14 }}><PeriodPicker value={period} onChange={onPeriodChange} /></div>
      <div style={{ textAlign: 'center', padding: '16px 0 10px' }}>
        <div style={{ fontSize: 52, fontWeight: 900, color, letterSpacing: -2, fontFamily: 'Impact, Arial Narrow, sans-serif', lineHeight: 1 }}>{val}</div>
        <div style={{ fontSize: 11, fontWeight: 700, color, marginTop: 4, textTransform: 'uppercase', letterSpacing: .5 }}>{rating}</div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>Time from ticket open → resolved</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 10, borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
        {[{ label: 'This Month', key: 'this_month' }, { label: 'Last Month', key: 'last_month' }, { label: 'This FY', key: 'this_fy' }].map(p => {
          const v = data?.[p.key]?.avg_closure_hours;
          const c = v == null ? 'var(--text-secondary)' : v <= 24 ? '#10b981' : v <= 72 ? '#f59e0b' : '#dc2626';
          return (
            <div key={p.key} style={{ textAlign: 'center', opacity: period === p.key ? 1 : 0.5, background: 'var(--bg-app)', borderRadius: 8, padding: '8px 4px' }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: c }}>{fmt(v)}</div>
              <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 2 }}>{p.label}</div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text-secondary)', display: 'flex', gap: 12, justifyContent: 'center' }}>
        <span style={{ color: '#10b981' }}>● ≤24h Excellent</span>
        <span style={{ color: '#f59e0b' }}>● ≤72h Good</span>
        <span style={{ color: '#dc2626' }}>● &gt;72h Needs work</span>
      </div>
    </div>
  );
}

function TicketCountWidget({ data, period, onPeriodChange }) {
  const d = data?.[period] || {};
  const total = d.total ?? 0;
  return (
    <div>
      <div style={{ marginBottom: 14 }}><PeriodPicker value={period} onChange={onPeriodChange} /></div>
      <div style={{ textAlign: 'center', padding: '12px 0' }}>
        <div style={{ fontSize: 64, fontWeight: 900, color: 'var(--status-resolved-text)', letterSpacing: -3, fontFamily: 'Impact, Arial Narrow, sans-serif', lineHeight: 1 }}>{total}</div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>Total tickets assigned</div>
      </div>
      <div style={{ marginTop: 10 }}>
        <StatBar label="Open"        value={d.open ?? 0}        total={total} color="var(--status-open-color)" />
        <StatBar label="In Progress" value={d.in_progress ?? 0} total={total} color="var(--status-progress-color)" />
        <StatBar label="Closed"      value={d.closed ?? 0}      total={total} color="#10b981" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 12, borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
        {[{ label: 'This Month', key: 'this_month' }, { label: 'Last Month', key: 'last_month' }, { label: 'This FY', key: 'this_fy' }].map(p => (
          <div key={p.key} style={{ textAlign: 'center', opacity: period === p.key ? 1 : 0.5, background: 'var(--bg-app)', borderRadius: 8, padding: '8px 4px' }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--status-resolved-text)' }}>{data?.[p.key]?.total ?? 0}</div>
            <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 2 }}>{p.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SlaComplianceWidget({ data, period, onPeriodChange }) {
  const d = data?.[period] || {};
  const total = d.total ?? 0;
  const breached = d.breached ?? 0;
  const compliant = Math.max(0, total - breached);
  const pct = total > 0 ? Math.round((compliant / total) * 100) : null;
  const color = pct == null ? 'var(--text-secondary)' : pct >= 90 ? '#10b981' : pct >= 70 ? '#f59e0b' : '#dc2626';
  const rating = pct == null ? 'No data' : pct >= 90 ? 'On Track' : pct >= 70 ? 'At Risk' : 'Critical';
  return (
    <div>
      <div style={{ marginBottom: 14 }}><PeriodPicker value={period} onChange={onPeriodChange} /></div>
      <div style={{ textAlign: 'center', padding: '12px 0 8px' }}>
        <div style={{ fontSize: 56, fontWeight: 900, color, letterSpacing: -2, fontFamily: 'Impact, Arial Narrow, sans-serif', lineHeight: 1 }}>{pct != null ? `${pct}%` : '—'}</div>
        <div style={{ fontSize: 11, fontWeight: 700, color, marginTop: 4, textTransform: 'uppercase', letterSpacing: .5 }}>{rating}</div>
      </div>
      <div style={{ margin: '12px 0' }}>
        <div style={{ position: 'relative', background: 'var(--n-100)', borderRadius: 8, height: 14 }}>
          <div style={{ width: `${pct ?? 0}%`, height: '100%', background: color, borderRadius: 8, transition: 'width .5s' }} />
          {pct != null && pct > 5 && (
            <span style={{ position: 'absolute', left: `calc(${pct}% - 4px)`, top: '50%', transform: 'translate(-100%,-50%)', fontSize: 10, fontWeight: 800, color: '#fff', paddingRight: 4 }}>{pct}%</span>
          )}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 4 }}>
        <div style={{ background: 'var(--action-bg)', borderRadius: 10, padding: '12px', textAlign: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 900, color: '#10b981', fontFamily: 'Impact, Arial Narrow, sans-serif' }}>{compliant}</div>
          <div style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>Compliant</div>
        </div>
        <div style={{ background: 'var(--red-50)', borderRadius: 10, padding: '12px', textAlign: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 900, color: '#dc2626', fontFamily: 'Impact, Arial Narrow, sans-serif' }}>{breached}</div>
          <div style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>Breached</div>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 12, borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
        {[{ label: 'This Month', key: 'this_month' }, { label: 'Last Month', key: 'last_month' }, { label: 'This FY', key: 'this_fy' }].map(p => {
          const pd = data?.[p.key] || {};
          const pp = pd.total > 0 ? Math.round(((pd.total - (pd.breached ?? 0)) / pd.total) * 100) : null;
          const pc = pp == null ? 'var(--text-secondary)' : pp >= 90 ? '#10b981' : pp >= 70 ? '#f59e0b' : '#dc2626';
          return (
            <div key={p.key} style={{ textAlign: 'center', opacity: period === p.key ? 1 : 0.5, background: 'var(--bg-app)', borderRadius: 8, padding: '8px 4px' }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: pc }}>{pp != null ? `${pp}%` : '—'}</div>
              <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 2 }}>{p.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ResolutionRateWidget({ data, period, onPeriodChange }) {
  const d = data?.[period] || {};
  const total = d.total ?? 0;
  const closed = d.closed ?? 0;
  const open = d.open ?? 0;
  const inProg = d.in_progress ?? 0;
  const pct = total > 0 ? Math.round((closed / total) * 100) : null;
  const color = pct == null ? 'var(--text-secondary)' : pct >= 70 ? '#10b981' : pct >= 40 ? '#f59e0b' : '#dc2626';
  return (
    <div>
      <div style={{ marginBottom: 14 }}><PeriodPicker value={period} onChange={onPeriodChange} /></div>
      <div style={{ textAlign: 'center', padding: '12px 0 8px' }}>
        <div style={{ fontSize: 56, fontWeight: 900, color, letterSpacing: -2, fontFamily: 'Impact, Arial Narrow, sans-serif', lineHeight: 1 }}>{pct != null ? `${pct}%` : '—'}</div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>of tickets resolved or closed</div>
      </div>
      <div style={{ margin: '12px 0' }}>
        <div style={{ display: 'flex', height: 14, borderRadius: 8, overflow: 'hidden', gap: 1 }}>
          {total > 0 ? (<>
            <div style={{ flex: closed, background: '#10b981', transition: 'flex .4s', minWidth: closed > 0 ? 2 : 0 }} title={`Closed: ${closed}`} />
            <div style={{ flex: inProg, background: 'var(--status-progress-color)', transition: 'flex .4s', minWidth: inProg > 0 ? 2 : 0 }} title={`In Progress: ${inProg}`} />
            <div style={{ flex: open, background: 'var(--status-open-color)', transition: 'flex .4s', minWidth: open > 0 ? 2 : 0 }} title={`Open: ${open}`} />
          </>) : <div style={{ flex: 1, background: 'var(--n-100)' }} />}
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 10, color: 'var(--text-secondary)', justifyContent: 'center' }}>
          <span><span style={{ color: '#10b981' }}>■</span> Closed {closed}</span>
          <span><span style={{ color: 'var(--status-progress-color)' }}>■</span> In Progress {inProg}</span>
          <span><span style={{ color: 'var(--status-open-color)' }}>■</span> Open {open}</span>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 8, borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
        {[{ label: 'This Month', key: 'this_month' }, { label: 'Last Month', key: 'last_month' }, { label: 'This FY', key: 'this_fy' }].map(p => {
          const pd = data?.[p.key] || {};
          const pp = pd.total > 0 ? Math.round(((pd.closed ?? 0) / pd.total) * 100) : null;
          const pc = pp == null ? 'var(--text-secondary)' : pp >= 70 ? '#10b981' : pp >= 40 ? '#f59e0b' : '#dc2626';
          return (
            <div key={p.key} style={{ textAlign: 'center', opacity: period === p.key ? 1 : 0.5, background: 'var(--bg-app)', borderRadius: 8, padding: '8px 4px' }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: pc }}>{pp != null ? `${pp}%` : '—'}</div>
              <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 2 }}>{p.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TrendChartWidget({ data, period, onPeriodChange }) {
  let rows = [];
  let labelKey = 'day';

  if (period === 'last_month') { rows = data?.last_month_daily || []; labelKey = 'day'; }
  else if (period === 'this_month') { rows = data?.this_month_daily || []; labelKey = 'day'; }
  else { rows = data?.this_fy_monthly || []; labelKey = 'month'; }

  const LINES = [
    { key: 'total',       label: 'Total',       color: '#6366f1' },
    { key: 'open',        label: 'Open',        color: 'var(--status-open-color)' },
    { key: 'in_progress', label: 'Pending',     color: 'var(--status-progress-color)' },
    { key: 'closed',      label: 'Closed',      color: '#10b981' },
  ];

  const shortLabel = (v) => {
    if (!v) return '';
    if (labelKey === 'day') return v.slice(8);
    return v.slice(5);
  };

  const W = 480, H = 160, PAD_L = 28, PAD_R = 8, PAD_T = 12, PAD_B = 24;
  const chartW = W - PAD_L - PAD_R;
  const chartH = H - PAD_T - PAD_B;

  const maxVal = Math.max(...rows.flatMap(r => LINES.map(l => r[l.key] || 0)), 1);
  const n = rows.length;

  function xPos(i) { return PAD_L + (n < 2 ? chartW / 2 : (i / (n - 1)) * chartW); }
  function yPos(v) { return PAD_T + chartH - (v / maxVal) * chartH; }

  function polyline(key) {
    if (n === 0) return '';
    return rows.map((r, i) => `${xPos(i).toFixed(1)},${yPos(r[key] || 0).toFixed(1)}`).join(' ');
  }

  // Y-axis gridlines
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(f => ({ v: Math.round(maxVal * f), y: yPos(maxVal * f) }));

  // X-axis: show at most 10 labels
  const step = Math.max(1, Math.ceil(n / 10));
  const xLabels = rows.map((r, i) => ({ i, label: shortLabel(r[labelKey]) })).filter((_, i) => i % step === 0);

  return (
    <div>
      <div style={{ marginBottom: 12 }}><PeriodPicker value={period} onChange={onPeriodChange} /></div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 10, flexWrap: 'wrap' }}>
        {LINES.map(l => (
          <div key={l.key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600 }}>
            <svg width="18" height="8"><line x1="0" y1="4" x2="18" y2="4" stroke={l.color} strokeWidth="2" /><circle cx="9" cy="4" r="2.5" fill={l.color} /></svg>
            {l.label}
          </div>
        ))}
      </div>

      {rows.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: 13 }}>No data for this period</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', minWidth: Math.max(rows.length * 14, 240), display: 'block' }}>
            {/* Grid lines */}
            {yTicks.map(t => (
              <g key={t.v}>
                <line x1={PAD_L} y1={t.y} x2={W - PAD_R} y2={t.y} stroke="rgba(0,0,0,.08)" strokeWidth="1" />
                <text x={PAD_L - 3} y={t.y + 3.5} textAnchor="end" fontSize="7" fill="#94a3b8">{t.v}</text>
              </g>
            ))}

            {/* X axis labels */}
            {xLabels.map(({ i, label }) => (
              <text key={i} x={xPos(i)} y={H - 4} textAnchor="middle" fontSize="7.5" fill="#94a3b8">{label}</text>
            ))}

            {/* Lines + dots */}
            {LINES.map(l => (
              <g key={l.key}>
                <polyline
                  points={polyline(l.key)}
                  fill="none"
                  stroke={l.color}
                  strokeWidth="1.8"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
                {rows.map((r, i) => (
                  <circle key={i} cx={xPos(i)} cy={yPos(r[l.key] || 0)} r="2.5" fill={l.color}>
                    <title>{l.label}: {r[l.key] || 0} ({r[labelKey]})</title>
                  </circle>
                ))}
              </g>
            ))}
          </svg>
        </div>
      )}
    </div>
  );
}

// ── Widget Card ────────────────────────────────────────────────────────────────
function WidgetCard({ wid, data, periods, onPeriodChange, editMode, isFirst, isLast, onMoveUp, onMoveDown, onRemove }) {
  const def = WIDGET_DEFS.find(d => d.id === wid);
  if (!def) return null;

  const renderContent = () => {
    const p = periods[wid] || 'this_month';
    const onChange = (v) => onPeriodChange(wid, v);
    switch (wid) {
      case 'ticket_status':  return <TicketStatusWidget   data={data} period={p} onPeriodChange={onChange} />;
      case 'response_time':  return <ResponseTimeWidget   data={data} period={p} onPeriodChange={onChange} />;
      case 'closure_time':   return <ClosureTimeWidget    data={data} period={p} onPeriodChange={onChange} />;
      case 'ticket_count':   return <TicketCountWidget    data={data} period={p} onPeriodChange={onChange} />;
      case 'trend_chart':    return <TrendChartWidget     data={data} period={p} onPeriodChange={onChange} />;
      case 'sla_compliance': return <SlaComplianceWidget  data={data} period={p} onPeriodChange={onChange} />;
      case 'resolution_rate':return <ResolutionRateWidget data={data} period={p} onPeriodChange={onChange} />;
      default: return null;
    }
  };

  return (
    <div style={{ background: 'var(--bg-surface)', borderRadius: 14, border: `1px solid ${editMode ? 'var(--action-hover)' : 'var(--border-subtle)'}`, boxShadow: '0 1px 4px rgba(15,23,42,.06)', overflow: 'hidden', transition: 'border-color .2s' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', background: editMode ? 'var(--action-bg)' : 'var(--bg-surface)' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-main)' }}>{def.label}</span>
        {editMode && (
          <div style={{ display: 'flex', gap: 4 }}>
            <button onClick={onMoveUp} disabled={isFirst} style={{ padding: '2px 9px', borderRadius: 6, border: '1px solid var(--border-subtle)', background: 'var(--bg-app)', cursor: isFirst ? 'default' : 'pointer', opacity: isFirst ? .3 : 1, fontSize: 13 }}>↑</button>
            <button onClick={onMoveDown} disabled={isLast} style={{ padding: '2px 9px', borderRadius: 6, border: '1px solid var(--border-subtle)', background: 'var(--bg-app)', cursor: isLast ? 'default' : 'pointer', opacity: isLast ? .3 : 1, fontSize: 13 }}>↓</button>
            <button onClick={onRemove} style={{ padding: '2px 9px', borderRadius: 6, border: '1px solid #fca5a5', background: 'rgba(239,68,68,.1)', color: '#dc2626', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>✕</button>
          </div>
        )}
      </div>
      <div style={{ padding: '14px 16px' }}>{renderContent()}</div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function EmployeeDashboard() {
  const isMobile = useIsMobile();
  const [filterType, setFilterType] = useState('employee');
  const [filterId, setFilterId]     = useState('');
  const [searchText, setSearchText] = useState('');
  const [employees, setEmployees]   = useState([]);
  const [products, setProducts]     = useState([]);
  const [categories, setCategories] = useState([]);
  const [empData, setEmpData]       = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [layout, setLayout]         = useState(DEFAULT_LAYOUT);
  const [editMode, setEditMode]     = useState(false);
  const [saving, setSaving]         = useState(false);
  const [showAdd, setShowAdd]       = useState(false);
  const [layoutLoaded, setLayoutLoaded] = useState(false);
  const [periods, setPeriods]       = useState({
    ticket_status: 'this_month',
    response_time: 'this_month',
    closure_time:  'this_month',
    ticket_count:  'this_month',
    trend_chart:   'this_fy',
  });

  // Load filter options and saved layout
  useEffect(() => {
    Promise.all([
      apiFetch('/users/agents'),
      apiFetch('/products'),
      apiFetch('/products/categories').catch(() => []),
      apiFetch('/analytics/dashboard-prefs').catch(() => null),
    ]).then(([agents, prods, cats, prefs]) => {
      const agentList = agents || [];
      setEmployees(agentList);
      setProducts(prods || []);
      setCategories(Array.isArray(cats) ? cats : []);
      if (prefs?.employee_layout?.length > 0) setLayout(prefs.employee_layout);
      setLayoutLoaded(true);
      // Auto-select first employee on load
      if (agentList.length > 0) {
        setFilterId(agentList[0].id);
        setSearchText(agentList[0].name);
      }
    });
  }, []);

  // Fetch analytics data when filter changes
  const fetchData = useCallback(async () => {
    if (!filterId) return;
    setDataLoading(true);
    try {
      const d = await apiFetch(`/analytics/employee?filter_type=${filterType}&filter_id=${filterId}`);
      setEmpData(d);
    } catch (_) { setEmpData(null); }
    setDataLoading(false);
  }, [filterType, filterId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Reset filterId when filterType changes
  useEffect(() => { setFilterId(''); setEmpData(null); setSearchText(''); }, [filterType]);

  async function saveLayout(nl) {
    setSaving(true);
    try { await apiFetch('/analytics/dashboard-prefs', { method: 'POST', body: { employee_layout: nl, type: 'employee' } }); } catch (_) {}
    setSaving(false);
  }

  function moveWidget(idx, dir) {
    const nl = [...layout];
    const si = idx + dir;
    if (si < 0 || si >= nl.length) return;
    [nl[idx], nl[si]] = [nl[si], nl[idx]];
    setLayout(nl);
  }

  function removeWidget(id) { setLayout(l => l.filter(w => w !== id)); }
  function addWidget(id)    { setLayout(l => l.includes(id) ? l : [...l, id]); setShowAdd(false); }
  function handleSave()     { saveLayout(layout); setEditMode(false); }
  function handleReset()    { const dl = [...DEFAULT_LAYOUT]; setLayout(dl); saveLayout(dl); setEditMode(false); }

  const hiddenWidgets = WIDGET_DEFS.filter(d => !layout.includes(d.id));

  const filterOptions = filterType === 'employee' ? employees
    : filterType === 'product' ? products
    : categories;

  const filterLabel = filterType === 'employee' ? 'Select Employee'
    : filterType === 'product' ? 'Select Product'
    : 'Select Category';

  const selectedName = filterOptions.find(o => o.id === filterId)?.name || '';

  return (
    <div className="page">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 20 }}>
        <div>
          <h1 className="page-title">Employee Dashboard</h1>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
            Performance analytics by employee, product or category
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {saving && <span style={{ fontSize: 12, color: 'var(--status-resolved-text)', fontWeight: 600 }}>Saving…</span>}
          {editMode ? (
            <>
              {hiddenWidgets.length > 0 && (
                <button onClick={() => setShowAdd(s => !s)} style={{ padding: '7px 14px', borderRadius: 8, border: '1.5px solid var(--action-hover)', background: 'var(--action-bg)', color: 'var(--status-resolved-text)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  + Add Widget
                </button>
              )}
              <button onClick={handleReset} style={{ padding: '7px 14px', borderRadius: 8, border: '1.5px solid var(--border-subtle)', background: 'var(--bg-surface)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Reset
              </button>
              <button onClick={handleSave} style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: '#0B6623', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                Save Layout
              </button>
            </>
          ) : (
            <button onClick={() => setEditMode(true)} style={{ padding: '7px 14px', borderRadius: 8, border: '1.5px solid var(--border-subtle)', background: 'var(--bg-surface)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              ⚙ Customise
            </button>
          )}
        </div>
      </div>

      {/* Add widget panel */}
      {showAdd && editMode && (
        <div style={{ background: 'var(--action-bg)', border: '1px solid var(--action-hover)', borderRadius: 10, padding: 14, marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {hiddenWidgets.map(w => (
            <button key={w.id} onClick={() => addWidget(w.id)} style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid var(--action-hover)', background: 'var(--bg-surface)', color: 'var(--status-resolved-text)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              + {w.label}
            </button>
          ))}
        </div>
      )}

      {/* Main two-panel layout */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '240px 1fr', gap: isMobile ? 14 : 20, alignItems: 'start' }}>

        {/* Left panel — type tabs + list */}
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, overflow: 'hidden', position: isMobile ? 'static' : 'sticky', top: 20 }}>
          {/* Type tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid var(--border-subtle)' }}>
            {[
              { value: 'employee', label: '👤 Staff' },
              { value: 'product',  label: '📦 Product' },
              { value: 'category', label: '🏷 Category' },
            ].map((t, i) => (
              <button key={t.value} onClick={() => setFilterType(t.value)} style={{
                flex: 1, padding: '10px 4px', border: 'none',
                borderRight: i < 2 ? '1px solid var(--border-subtle)' : 'none',
                background: filterType === t.value ? 'var(--status-resolved-text)' : 'var(--bg-surface)',
                color: filterType === t.value ? '#fff' : 'var(--text-secondary)',
                fontSize: 11, fontWeight: 700, cursor: 'pointer', transition: 'all .15s',
              }}>{t.label}</button>
            ))}
          </div>

          {/* Search */}
          <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border-subtle)' }}>
            <input
              className="input"
              style={{ fontSize: 12, padding: '6px 10px' }}
              placeholder={`Search ${filterType}s…`}
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              autoComplete="off"
            />
          </div>

          {/* List */}
          <div style={{ maxHeight: isMobile ? 220 : 480, overflowY: 'auto' }}>
            {filterOptions.length === 0 ? (
              <div style={{ padding: '20px 14px', fontSize: 12, color: 'var(--text-secondary)', textAlign: 'center' }}>No {filterType}s found</div>
            ) : (
              filterOptions
                .filter(o => !searchText.trim() || o.name.toLowerCase().includes(searchText.toLowerCase()))
                .map(o => {
                  const isSelected = o.id === filterId;
                  return (
                    <div
                      key={o.id}
                      onClick={() => { setFilterId(o.id); setEmpData(null); }}
                      style={{
                        padding: '10px 14px', cursor: 'pointer',
                        borderBottom: '1px solid var(--border-subtle)',
                        background: isSelected ? 'var(--action-bg)' : 'var(--bg-surface)',
                        borderLeft: isSelected ? '3px solid var(--status-resolved-text)' : '3px solid transparent',
                        transition: 'background .1s',
                      }}
                      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'var(--bg-app)'; }}
                      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'var(--bg-surface)'; }}
                    >
                      <div style={{ fontSize: 13, fontWeight: isSelected ? 700 : 500, color: isSelected ? 'var(--status-resolved-text)' : 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.name}</div>
                    </div>
                  );
                })
            )}
          </div>
        </div>

        {/* Right panel — widgets */}
        <div>
          {!filterId && (
            <div style={{ textAlign: 'center', padding: '80px 20px', color: 'var(--text-secondary)' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>📊</div>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Select from the list</div>
              <div style={{ fontSize: 13 }}>Choose an employee, product, or category to view performance analytics</div>
            </div>
          )}

          {filterId && dataLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
              <div className="spinner" />
            </div>
          )}

          {filterId && !dataLoading && layoutLoaded && (
            <>
              <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
                  {selectedName}
                  <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 8, textTransform: 'capitalize' }}>{filterType}</span>
                </div>
                <button onClick={fetchData} style={{ padding: '5px 12px', borderRadius: 7, border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)', color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer' }}>↻ Refresh</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(340px, 1fr))', gap: isMobile ? 12 : 16 }}>
                {layout.map((wid, idx) => (
                  <WidgetCard
                    key={wid}
                    wid={wid}
                    data={empData}
                    periods={periods}
                    onPeriodChange={(w, v) => setPeriods(p => ({ ...p, [w]: v }))}
                    editMode={editMode}
                    isFirst={idx === 0}
                    isLast={idx === layout.length - 1}
                    onMoveUp={() => moveWidget(idx, -1)}
                    onMoveDown={() => moveWidget(idx, 1)}
                    onRemove={() => removeWidget(wid)}
                  />
                ))}
              </div>
              {empData && empData.this_month?.total === 0 && empData.this_fy?.total === 0 && (
                <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)', background: 'var(--bg-surface)', borderRadius: 12, marginTop: 8, border: '1px solid var(--border-subtle)' }}>
                  No ticket data found for this selection
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
